/**
 * The WebGL context and the plumbing every layer shares: programs,
 * attributes, blending, buffers and instanced drawing.
 *
 * WebGL 2 where there is one, else WebGL 1 with instancing; nothing less.
 * The context can be lost at any time: drawing then stops until the browser
 * restores it, when everything is built again.
 */
import { SHADERS, type ProgramName, type ShaderSource } from './shaders'

/** How a draw combines with what is already there. */
export type BlendMode = 'add' | 'over' | 'grain'

/** The size of the page in CSS pixels, and device pixels per CSS pixel. */
export interface Viewport {
  readonly width: number
  readonly height: number
  readonly dpr: number
}

export interface Program {
  readonly program: WebGLProgram
  readonly uniforms: Map<string, WebGLUniformLocation | null>
  readonly attributes: Map<string, number>
}

/** A vertex attribute: name, buffer, size, stride and offset (in floats), instance divisor. */
export type AttributeBinding = [
  name: string,
  buffer: WebGLBuffer,
  size: number,
  stride: number,
  offset: number,
  divisor?: number,
]

export interface Gpu {
  readonly gl: WebGLRenderingContext
  /** Whether the context is lost; nothing can be drawn until it is restored. */
  lost(): boolean
  /** Compiles the programs and resets the shared state, as on a new context. */
  reset(): void
  program(name: ProgramName): Program
  /** Makes a program current and gives it the viewport. */
  use(program: Program, viewport: Viewport): void
  uniform(program: Program, name: string): WebGLUniformLocation | null
  /** Binds exactly these attributes, switching off the ones bound before. */
  bind(program: Program, list: AttributeBinding[]): void
  blend(mode: BlendMode): void
  staticBuffer(data: Float32Array): WebGLBuffer
  drawInstanced(vertices: number, instances: number): void
}

const OPTIONS: WebGLContextAttributes = {
  alpha: false,
  antialias: true,
  depth: false,
  stencil: false,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  powerPreference: 'high-performance',
}

/** The context on this canvas, or `null` without WebGL. `onRestore` runs when a lost context comes back. */
export function createGpu(canvas: HTMLCanvasElement, onRestore: () => void): Gpu | null {
  let gl2: WebGL2RenderingContext | null = null
  let ext: ANGLE_instanced_arrays | null = null
  try {
    gl2 = canvas.getContext('webgl2', OPTIONS)
  } catch {
    gl2 = null
  }
  let gl: WebGLRenderingContext | null = gl2
  if (!gl) {
    try {
      gl =
        canvas.getContext('webgl', OPTIONS) ??
        (canvas.getContext('experimental-webgl', OPTIONS) as WebGLRenderingContext | null)
    } catch {
      gl = null
    }
    if (!gl) return null
    ext = gl.getExtension('ANGLE_instanced_arrays')
    if (!ext) return null
  }
  const context = gl
  let lost = false
  canvas.addEventListener(
    'webglcontextlost',
    (e) => {
      e.preventDefault()
      lost = true
    },
    false
  )
  canvas.addEventListener(
    'webglcontextrestored',
    () => {
      lost = false
      onRestore()
    },
    false
  )
  const programs = new Map<ProgramName, Program>()
  let current: Program | null = null
  let blendNow: BlendMode | null = null
  // attributes: which are on, and at what divisor (shared state in WebGL 1, so set every time)
  let enabled: number[] = []

  return {
    gl: context,
    lost: () => lost,
    reset() {
      current = null
      blendNow = null
      enabled = []
      for (const name of Object.keys(SHADERS) as ProgramName[])
        programs.set(name, link(SHADERS[name]))
      context.enable(context.BLEND)
    },
    program: (name) => programs.get(name)!,
    use(program, viewport) {
      if (current !== program) {
        context.useProgram(program.program)
        current = program
      }
      context.uniform2f(uniform(program, 'uRes'), viewport.width, viewport.height)
      context.uniform1f(uniform(program, 'uDpr'), viewport.dpr)
    },
    uniform,
    bind(program, list) {
      for (const location of enabled) {
        context.disableVertexAttribArray(location)
        divisor(location, 0)
      }
      enabled = []
      for (const [name, buffer, size, stride, offset, instanceDivisor] of list) {
        const location = attribute(program, name)
        if (location < 0) continue
        context.bindBuffer(context.ARRAY_BUFFER, buffer)
        context.enableVertexAttribArray(location)
        context.vertexAttribPointer(location, size, context.FLOAT, false, stride * 4, offset * 4)
        divisor(location, instanceDivisor ?? 0)
        enabled.push(location)
      }
    },
    blend(mode) {
      if (mode === blendNow) return
      blendNow = mode
      if (mode === 'add') context.blendFunc(context.ONE, context.ONE)
      else if (mode === 'over') context.blendFunc(context.ONE, context.ONE_MINUS_SRC_ALPHA)
      else context.blendFunc(context.DST_COLOR, context.SRC_COLOR)
    },
    staticBuffer(data) {
      const buffer = context.createBuffer()
      context.bindBuffer(context.ARRAY_BUFFER, buffer)
      context.bufferData(context.ARRAY_BUFFER, data, context.STATIC_DRAW)
      return buffer
    },
    drawInstanced(vertices, instances) {
      if (instances <= 0) return
      if (gl2) gl2.drawArraysInstanced(context.TRIANGLES, 0, vertices, instances)
      else ext!.drawArraysInstancedANGLE(context.TRIANGLES, 0, vertices, instances)
    },
  }

  function attribute(program: Program, name: string) {
    let location = program.attributes.get(name)
    if (location === undefined) {
      location = context.getAttribLocation(program.program, name)
      program.attributes.set(name, location)
    }
    return location
  }

  function uniform(program: Program, name: string) {
    let location = program.uniforms.get(name)
    if (location === undefined) {
      location = context.getUniformLocation(program.program, name)
      program.uniforms.set(name, location)
    }
    return location
  }

  function link([vertex, fragment]: ShaderSource): Program {
    const program = context.createProgram()
    context.attachShader(program, compile(context.VERTEX_SHADER, vertex))
    context.attachShader(program, compile(context.FRAGMENT_SHADER, fragment))
    context.linkProgram(program)
    if (!context.getProgramParameter(program, context.LINK_STATUS) && !context.isContextLost())
      throw new Error(context.getProgramInfoLog(program) ?? '')
    return { program, uniforms: new Map(), attributes: new Map() }
  }

  function compile(type: number, source: string) {
    const shader = context.createShader(type)!
    context.shaderSource(shader, source)
    context.compileShader(shader)
    if (!context.getShaderParameter(shader, context.COMPILE_STATUS) && !context.isContextLost())
      throw new Error(context.getShaderInfoLog(shader) + '\n' + source)
    return shader
  }

  function divisor(location: number, n: number) {
    if (gl2) gl2.vertexAttribDivisor(location, n)
    else ext!.vertexAttribDivisorANGLE(location, n)
  }
}
